import { useCallback } from 'react'

const API_KEY = import.meta.env.VITE_GOOGLE_PLACES_API_KEY
const BASE_URL = 'https://places.googleapis.com/v1'

export default function useGooglePlaces() {
  const getCityPredictions = useCallback(
    (input) => autocomplete(input, { types: ['locality'] }),
    [],
  )

  const getPlacePredictions = useCallback(
    (input, locationHint) => autocomplete(input, { types: ['establishment'], locationHint }),
    [],
  )

  const getAddressPredictions = useCallback(
    (input, locationHint) => autocomplete(input, { types: ['street_address'], locationHint }),
    [],
  )

  const resolvePlace = useCallback(async (placeId) => {
    if (!API_KEY || !placeId) return null
    try {
      const res = await fetch(`${BASE_URL}/places/${placeId}`, {
        headers: {
          'X-Goog-Api-Key': API_KEY,
          'X-Goog-FieldMask': 'displayName,formattedAddress,addressComponents',
        },
      })
      const data = await res.json()
      if (!data.addressComponents) return null

      const parsed = parseAddressComponents(data.addressComponents)
      return {
        name: data.displayName?.text || '',
        address: buildStreetAddress(parsed),
        address_2: parsed.subpremise,
        city: parsed.city,
        state: parsed.state,
        postal_code: parsed.postal_code,
      }
    } catch {
      return null
    }
  }, [])

  const validateAddress = useCallback(async ({ address, city, state, postal_code }) => {
    if (!API_KEY) return { valid: true }
    if (!address?.trim()) return { valid: false, reason: 'empty' }
    const cityStateZip = [city, state, postal_code].filter(Boolean).join(', ')
    try {
      const res = await fetch(`https://addressvalidation.googleapis.com/v1:validateAddress?key=${API_KEY}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          address: {
            regionCode: 'US',
            addressLines: [address, cityStateZip].filter(Boolean),
          },
          enableUspsCass: true,
        }),
      })
      const data = await res.json()
      if (data.error) {
        console.warn('[validateAddress] api error:', data.error.status, data.error.message)
        return { valid: false, reason: 'api_error' }
      }

      const result = data.result
      const verdict = result?.verdict || {}
      const postal = result?.address?.postalAddress || {}
      const uspsDpv = result?.uspsData?.dpvConfirmation
      const stripZipPlus4 = (zip) => (zip || '').split('-')[0]
      const formattedAddress = (result?.address?.formattedAddress || '').replace(/(\d{5})-\d{4}/g, '$1')

      const normalized = {
        address: (postal.addressLines || [])[0] || '',
        address_2: (postal.addressLines || [])[1] || '',
        city: postal.locality || '',
        state: postal.administrativeArea || '',
        postal_code: stripZipPlus4(postal.postalCode),
      }

      const granularity = verdict.validationGranularity
      const isPrecise = granularity === 'PREMISE' || granularity === 'SUB_PREMISE'
      const hasCleanup = verdict.hasReplacedComponents || verdict.hasInferredComponents
      const uspsRejected = uspsDpv === 'N'

      if (!isPrecise || verdict.hasUnconfirmedComponents || !verdict.addressComplete || uspsRejected) {
        return { valid: false, reason: 'not_found' }
      }
      if (hasCleanup) {
        return { valid: false, reason: 'partial', formattedAddress, normalized }
      }
      return { valid: true, formattedAddress, normalized }
    } catch (err) {
      console.warn('[validateAddress] network error:', err)
      return { valid: false, reason: 'network_error' }
    }
  }, [])

  const searchZip = useCallback(async (zip) => {
    if (!API_KEY || !/^\d{5}$/.test(zip)) return null
    try {
      const res = await fetch(`${BASE_URL}/places:autocomplete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Goog-Api-Key': API_KEY },
        body: JSON.stringify({
          input: zip,
          includedRegionCodes: ['us'],
          includedPrimaryTypes: ['postal_code'],
        }),
      })
      const data = await res.json()
      const placeId = data.suggestions?.[0]?.placePrediction?.placeId
      if (!placeId) return null
      return resolvePlace(placeId)
    } catch {
      return null
    }
  }, [resolvePlace])

  return {
    isLoaded: !!API_KEY,
    getCityPredictions,
    getPlacePredictions,
    getAddressPredictions,
    resolvePlace,
    searchZip,
    validateAddress,
  }
}

async function autocomplete(input, { types, locationHint } = {}) {
  if (!API_KEY || !input || input.length < 2) return []
  try {
    const body = {
      input: locationHint ? `${input}, ${locationHint}` : input,
      includedRegionCodes: ['us'],
    }
    if (types) body.includedPrimaryTypes = types
    const res = await fetch(`${BASE_URL}/places:autocomplete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Goog-Api-Key': API_KEY },
      body: JSON.stringify(body),
    })
    const data = await res.json()
    return data.suggestions?.map((s) => s.placePrediction) || []
  } catch {
    return []
  }
}

function parseAddressComponents(components) {
  const result = { streetNumber: '', route: '', subpremise: '', city: '', state: '', postal_code: '' }
  for (const c of components) {
    if (c.types.includes('street_number')) result.streetNumber = c.longText
    else if (c.types.includes('route')) result.route = c.longText
    else if (c.types.includes('subpremise')) result.subpremise = c.longText
    else if (c.types.includes('locality')) result.city = c.longText
    else if (c.types.includes('administrative_area_level_1')) result.state = c.shortText
    else if (c.types.includes('postal_code')) result.postal_code = c.longText
  }
  return result
}

function buildStreetAddress(parsed) {
  return [parsed.streetNumber, parsed.route].filter(Boolean).join(' ')
}
